/**
 * KnowledgeBasePage - Admin interface for managing AI Knowledge Base (RAG)
 * Allows uploading HOA documents for semantic search and AI context
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import { Alert, AlertDescription } from "../components/ui/alert";

import {
  Brain,
  Upload,
  Search,
  FileText,
  Trash2,
  Database,
  Sparkles,
  BookOpen,
  ArrowLeft,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  BarChart3,
  RefreshCw
} from "lucide-react";

const DOCUMENT_TYPE_LABELS = {
  declaration: "Declaration of Covenants",
  ccrs: "CC&Rs (Covenants, Conditions & Restrictions)",
  bylaws: "Bylaws",
  design_guidelines: "Design Guidelines",
  rules_regulations: "Rules & Regulations",
  amendments: "Recorded Amendments",
  meeting_minutes: "Meeting Minutes",
  policies: "HOA Policies",
  financial: "Financial Documents",
  other: "Other Documents"
};

const KnowledgeBasePageRAG = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // State
  const [activeTab, setActiveTab] = useState("documents");
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Upload state
  const [uploadMode, setUploadMode] = useState("text"); // "text" or "file"
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadContent, setUploadContent] = useState("");
  const [uploadType, setUploadType] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchDocType, setSearchDocType] = useState("__all__");
  
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, doc: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [docsRes, statsRes] = await Promise.all([
        axios.get(`${API}/rag/documents`, { headers }),
        axios.get(`${API}/rag/stats`, { headers })
      ]);
      setDocuments(docsRes.data.documents || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load knowledge base data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadTitle || !uploadType) {
      toast.error("Please provide a title and document type");
      return;
    }

    if (uploadMode === "text" && !uploadContent) {
      toast.error("Please provide document content");
      return;
    }

    if (uploadMode === "file" && !uploadFile) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);
    try {
      if (uploadMode === "text") {
        await axios.post(`${API}/rag/documents/upload`, {
          title: uploadTitle,
          content: uploadContent,
          document_type: uploadType,
          metadata: {}
        }, { headers });
      } else {
        const formData = new FormData();
        formData.append("file", uploadFile);
        formData.append("title", uploadTitle);
        formData.append("document_type", uploadType);
        
        await axios.post(`${API}/rag/documents/upload-file`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" }
        });
      }

      toast.success("Document uploaded and indexed successfully!");
      setUploadTitle("");
      setUploadContent("");
      setUploadType("");
      setUploadFile(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a search query");
      return;
    }

    setSearching(true);
    try {
      const res = await axios.post(`${API}/rag/search`, {
        query: searchQuery,
        document_type: searchDocType === "__all__" ? null : searchDocType,
        limit: 10,
        min_score: 0.1
      }, { headers });

      setSearchResults(res.data.results || []);
      
      if (res.data.results?.length === 0) {
        toast.info("No matching documents found");
      }
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.doc) return;

    try {
      await axios.delete(`${API}/rag/documents/${deleteDialog.doc.doc_id}`, { headers });
      toast.success("Document deleted");
      setDeleteDialog({ open: false, doc: null });
      loadData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  if (loading) {
    return (
      <AdminSidebar>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
      <div className="min-h-screen bg-background" data-testid="knowledge-base-rag-page">
        {/* Header */}
        <header className="border-b bg-card sticky top-0 z-10">
          <div className="px-4 md:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="flex items-center gap-2">
                  <Brain className="w-6 h-6 text-primary" />
                  <div>
                    <h1 className="font-serif text-xl font-semibold">AI Knowledge Base</h1>
                    <p className="text-sm text-muted-foreground">RAG-powered document intelligence</p>
                  </div>
                </div>
              </div>
              <Button variant="outline" onClick={loadData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-8 py-6">
          {/* AI Training Progress Banner */}
          {stats && (
            <Card className="mb-6 border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                      <Brain className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">AI Knowledge Training</h3>
                      <p className="text-sm text-muted-foreground">
                        Your AI is trained on {stats.documents?.total_documents || 0} documents 
                        ({stats.documents?.total_chunks || 0} indexed chunks)
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-green-600">Semantic Search Active</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Using sentence-transformers embeddings
                    </p>
                  </div>
                </div>
                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>Knowledge Coverage</span>
                    <span>
                      {Object.keys(stats.documents?.documents_by_type || {}).length} of {Object.keys(DOCUMENT_TYPE_LABELS).length} document types
                    </span>
                  </div>
                  <Progress 
                    value={(Object.keys(stats.documents?.documents_by_type || {}).length / Object.keys(DOCUMENT_TYPE_LABELS).length) * 100} 
                    className="h-2"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Overview */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.documents?.total_documents || 0}</p>
                      <p className="text-sm text-muted-foreground">Documents</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-secondary/10">
                      <Database className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.documents?.total_chunks || 0}</p>
                      <p className="text-sm text-muted-foreground">Indexed Chunks</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-accent/10">
                      <Sparkles className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.memories?.total_memories || 0}</p>
                      <p className="text-sm text-muted-foreground">AI Memories</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/10">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">Active</p>
                      <p className="text-sm text-muted-foreground">RAG Status</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="documents">
                <BookOpen className="w-4 h-4 mr-2" />
                Documents
              </TabsTrigger>
              <TabsTrigger value="upload">
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </TabsTrigger>
              <TabsTrigger value="search">
                <Search className="w-4 h-4 mr-2" />
                Test Search
              </TabsTrigger>
            </TabsList>

            {/* Documents Tab */}
            <TabsContent value="documents" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Indexed Documents</CardTitle>
                  <CardDescription>
                    Documents available for AI semantic search and context
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {documents.length === 0 ? (
                    <Alert>
                      <AlertCircle className="w-4 h-4" />
                      <AlertDescription>
                        No documents indexed yet. Upload HOA documents to enable AI-powered search.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-3">
                        {documents.map((doc) => (
                          <div
                            key={doc.doc_id}
                            className="p-4 border rounded-lg flex items-start justify-between"
                          >
                            <div className="flex-1">
                              <h4 className="font-semibold">{doc.title}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="secondary">
                                  {DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {doc.total_chunks} chunks
                                </span>
                              </div>
                              {doc.content_preview && (
                                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                                  {doc.content_preview}
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(doc.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => setDeleteDialog({ open: true, doc })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Upload Tab */}
            <TabsContent value="upload" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Upload Document</CardTitle>
                  <CardDescription>
                    Add HOA documents to the AI knowledge base for semantic search
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Button
                      variant={uploadMode === "text" ? "default" : "outline"}
                      onClick={() => setUploadMode("text")}
                    >
                      Paste Text
                    </Button>
                    <Button
                      variant={uploadMode === "file" ? "default" : "outline"}
                      onClick={() => setUploadMode("file")}
                    >
                      Upload File
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Document Title</Label>
                      <Input
                        value={uploadTitle}
                        onChange={(e) => setUploadTitle(e.target.value)}
                        placeholder="e.g., Declaration of Covenants 2024"
                      />
                    </div>
                    <div>
                      <Label>Document Type</Label>
                      <Select value={uploadType} onValueChange={setUploadType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {uploadMode === "text" ? (
                    <div>
                      <Label>Document Content</Label>
                      <Textarea
                        value={uploadContent}
                        onChange={(e) => setUploadContent(e.target.value)}
                        placeholder="Paste the full document text here..."
                        className="h-64 font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {uploadContent.length} characters
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Label>Document File (.txt, .md)</Label>
                      <Input
                        type="file"
                        accept=".txt,.md"
                        onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      />
                      {uploadFile && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Selected: {uploadFile.name}
                        </p>
                      )}
                    </div>
                  )}

                  <Button onClick={handleUpload} disabled={uploading} className="w-full">
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload & Index Document
                      </>
                    )}
                  </Button>

                  <Alert>
                    <Sparkles className="w-4 h-4" />
                    <AlertDescription>
                      Documents are automatically split into chunks and embedded for semantic search.
                      The AI will use these documents to provide accurate, document-grounded answers.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Search Tab */}
            <TabsContent value="search" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Test Semantic Search</CardTitle>
                  <CardDescription>
                    Test how the AI finds relevant information from indexed documents
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="e.g., What are the rules about fences?"
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                      />
                    </div>
                    <Select value={searchDocType} onValueChange={setSearchDocType}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="All types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All Document Types</SelectItem>
                        {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={handleSearch} disabled={searching}>
                      {searching ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                    </Button>
                  </div>

                  {searchResults.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        Found {searchResults.length} relevant chunks
                      </p>
                      {searchResults.map((result, idx) => (
                        <Card key={idx} className="border-l-4 border-l-primary">
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h4 className="font-semibold">{result.title}</h4>
                                <Badge variant="outline" className="mt-1">
                                  {DOCUMENT_TYPE_LABELS[result.document_type] || result.document_type}
                                </Badge>
                              </div>
                              <Badge
                                variant={result.score > 0.7 ? "default" : result.score > 0.5 ? "secondary" : "outline"}
                              >
                                {(result.score * 100).toFixed(0)}% match
                              </Badge>
                            </div>
                            <p className="text-sm mt-2 text-muted-foreground">
                              {result.content}
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, doc: deleteDialog.doc })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Document</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{deleteDialog.doc?.title}"? This will remove all indexed chunks and cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, doc: null })}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminSidebar>
  );
};

export default KnowledgeBasePageRAG;
